import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

// Define which roles have admin access
const ADMIN_ROLES = ['system_admin', 'manager', 'warehouse', 'accounting', 'admin'];
const CUSTOMER_ROLES = ['customer_admin', 'regular_user', 'customer'];

const AuthGuard = ({ children, requiredRole }) => {
  const { user, loading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Helper function to check if user has required role access
  const hasRequiredAccess = (userRole, requiredRole) => {
    if (!requiredRole) return true; // No role requirement
    
    // Handle array of required roles
    if (Array.isArray(requiredRole)) {
      return requiredRole.includes(userRole);
    }
    
    // Handle specific role groups
    switch (requiredRole) {
      case 'admin':
        return ADMIN_ROLES.includes(userRole);
      case 'customer':
        return CUSTOMER_ROLES.includes(userRole);
      default:
        // Exact role match
        return userRole === requiredRole;
    }
  };

  // Wait until authentication is checked
  if (loading) {
    return <div>Checking authentication...</div>;
  }

  // Check if user is authenticated
  if (!isAuthenticated() || !user) {
    // Create redirect URL that includes the original destination
    const returnUrl = encodeURIComponent(window.location.pathname);
    window.location.href = `/login?returnUrl=${returnUrl}`;
    return null;
  }

  // Get user role from user object or roles array - DON'T use user_type as role
  const userRole = user.role || (user.roles && user.roles[0]?.name) || null;
  
  // If user doesn't have the required role access
  if (requiredRole && !hasRequiredAccess(userRole, requiredRole)) {
    // Handle users without roles - they should get an error page, not redirected to another protected route
    if (!userRole) {
      return <Navigate to="/auth-error" replace />;
    }
    
    // Handle users with roles - redirect to appropriate dashboard
    if (ADMIN_ROLES.includes(userRole)) {
      return <Navigate to="/admin" replace />;
    } else if (CUSTOMER_ROLES.includes(userRole)) {
      return <Navigate to="/customer" replace />;
    } else {
      return <Navigate to="/auth-error" replace />;
    }
  }

  return children;
};

export default AuthGuard; 